import { UserMax } from "../handlers/types";

const STORAGE_KEYS = {
  CHARACTER: "game_character",
  USER: "user",
  FIRST_PLAY_COMPLETED: "first_play_completed",
  LAST_VISIT_TIME: "last_visit_time",
  SPECIAL_DEALS_SHOWN_TODAY: "special_deals_shown_today",
  DAILY_MAX_ROUNDS_VALUE: "daily_max_rounds_value",
  DAILY_MAX_ROUNDS_RESET_AT: "daily_max_rounds_reset_at",
  GEMS_TOTAL: "gems_total",
} as const;

export class StorageService {
  /**
   * Save a single character to local storage
   */
  // Character-related methods are part of legacy game code and require
  // Character types. Keep them typed as any here to avoid coupling.
  static saveCharacter(character: any): void {
    try {
      const serializedCharacter = JSON.stringify(character.toJSON());
      localStorage.setItem(STORAGE_KEYS.CHARACTER, serializedCharacter);
    } catch (error) {
      console.error("Failed to save character:", error);
    }
  }

  /**
   * Load a single character from local storage
   */
  static loadCharacter(): any | null {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.CHARACTER);
      if (!saved) return null;
      const characterData = JSON.parse(saved);
      return characterData;
    } catch (error) {
      console.error("Failed to load character:", error);
      return null;
    }
  }

  static saveUser(user: UserMax): void {
    try {
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
    } catch (error) {
      console.error("Failed to save user:", error);
    }
  }

  static loadUser(): UserMax | null {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.USER);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error("Failed to load user:", error);
      return null;
    }
  }

  /**
   * Check if the user has completed their first play
   */
  static hasCompletedFirstPlay(): boolean {
    try {
      return localStorage.getItem(STORAGE_KEYS.FIRST_PLAY_COMPLETED) === "true";
    } catch (error) {
      console.error("Failed to check first play status:", error);
      return false;
    }
  }

  /**
   * Mark that the user has completed their first play
   */
  static setFirstPlayCompleted(): void {
    try {
      localStorage.setItem(STORAGE_KEYS.FIRST_PLAY_COMPLETED, "true");
    } catch (error) {
      console.error("Failed to set first play status:", error);
    }
  }

  /**
   * Get the last visit time
   */
  static getLastVisitTime(): number | null {
    try {
      const lastVisit = localStorage.getItem(STORAGE_KEYS.LAST_VISIT_TIME);
      return lastVisit ? parseInt(lastVisit, 10) : null;
    } catch (error) {
      console.error("Failed to get last visit time:", error);
      return null;
    }
  }

  /**
   * Update the last visit time to current time
   */
  static updateLastVisitTime(): void {
    try {
      const currentTime = Date.now();
      localStorage.setItem(
        STORAGE_KEYS.LAST_VISIT_TIME,
        currentTime.toString()
      );
    } catch (error) {
      console.error("Failed to update last visit time:", error);
    }
  }

  /**
   * Check if user should see special deals (24+ hours since last visit)
   * @returns true if user should see special deals
   */
  static shouldShowSpecialDeals(): boolean {
    try {
      const lastVisit = this.getLastVisitTime();
      const now = Date.now();

      // If no last visit recorded, don't show special deals (first time user)
      if (!lastVisit) {
        return false;
      }

      // Check if 24 hours (86400000 ms) have passed
      const timeDifference = now - lastVisit;
      const twentyFourHours = 24 * 60 * 60 * 1000;

      return timeDifference >= twentyFourHours;
    } catch (error) {
      console.error("Failed to check special deals eligibility:", error);
      return false;
    }
  }

  /**
   * Check if special deals have been shown today
   */
  static hasSpecialDealsBeenShownToday(): boolean {
    try {
      const shownToday = localStorage.getItem(
        STORAGE_KEYS.SPECIAL_DEALS_SHOWN_TODAY
      );
      if (!shownToday) return false;

      const shownDate = new Date(parseInt(shownToday, 10));
      const today = new Date();

      // Check if it's the same day
      return (
        shownDate.getDate() === today.getDate() &&
        shownDate.getMonth() === today.getMonth() &&
        shownDate.getFullYear() === today.getFullYear()
      );
    } catch (error) {
      console.error("Failed to check if special deals shown today:", error);
      return false;
    }
  }

  /**
   * Mark special deals as shown today
   */
  static markSpecialDealsShownToday(): void {
    try {
      const now = Date.now();
      localStorage.setItem(
        STORAGE_KEYS.SPECIAL_DEALS_SHOWN_TODAY,
        now.toString()
      );
    } catch (error) {
      console.error("Failed to mark special deals as shown:", error);
    }
  }

  /**
   * Initialize session tracking - should be called when app starts
   */
  static initializeSession(): { shouldShowSpecialDeals: boolean } {
    try {
      const shouldShow =
        this.shouldShowSpecialDeals() && !this.hasSpecialDealsBeenShownToday();

      // Update last visit time for next session
      this.updateLastVisitTime();

      return { shouldShowSpecialDeals: shouldShow };
    } catch (error) {
      console.error("Failed to initialize session:", error);
      // Update last visit time even if check failed
      this.updateLastVisitTime();
      return { shouldShowSpecialDeals: false };
    }
  }

  /**
   * Get the max round level achieved within the last 24 hours.
   * Resets the stored value if the 24h window has elapsed.
   */
  static getDailyMaxRounds(): number {
    try {
      const now = Date.now();
      const resetAtRaw = localStorage.getItem(
        STORAGE_KEYS.DAILY_MAX_ROUNDS_RESET_AT
      );
      const resetAt = resetAtRaw ? parseInt(resetAtRaw, 10) : 0;
      const twentyFourHours = 24 * 60 * 60 * 1000;

      if (!resetAt || now - resetAt >= twentyFourHours) {
        localStorage.setItem(STORAGE_KEYS.DAILY_MAX_ROUNDS_VALUE, "0");
        localStorage.setItem(
          STORAGE_KEYS.DAILY_MAX_ROUNDS_RESET_AT,
          now.toString()
        );
        return 0;
      }

      const valRaw = localStorage.getItem(STORAGE_KEYS.DAILY_MAX_ROUNDS_VALUE);
      const value = valRaw ? parseInt(valRaw, 10) : 0;
      return Number.isFinite(value) && value >= 0 ? value : 0;
    } catch (error) {
      console.error("Failed to get daily max rounds:", error);
      return 0;
    }
  }

  /**
   * Update the daily max rounds value if the provided level is higher.
   * Also ensures the 24h window is maintained/reset as needed.
   */
  static updateDailyMaxRoundsIfHigher(level: number): number {
    try {
      const safeLevel = Math.max(0, Math.floor(level));
      const now = Date.now();
      const resetAtRaw = localStorage.getItem(
        STORAGE_KEYS.DAILY_MAX_ROUNDS_RESET_AT
      );
      const resetAt = resetAtRaw ? parseInt(resetAtRaw, 10) : 0;
      const twentyFourHours = 24 * 60 * 60 * 1000;

      if (!resetAt || now - resetAt >= twentyFourHours) {
        localStorage.setItem(
          STORAGE_KEYS.DAILY_MAX_ROUNDS_VALUE,
          safeLevel.toString()
        );
        localStorage.setItem(
          STORAGE_KEYS.DAILY_MAX_ROUNDS_RESET_AT,
          now.toString()
        );
        if (safeLevel > 0) {
          this.addGems(safeLevel);
        }
        return safeLevel;
      }

      const current = this.getDailyMaxRounds();
      if (safeLevel > current) {
        localStorage.setItem(
          STORAGE_KEYS.DAILY_MAX_ROUNDS_VALUE,
          safeLevel.toString()
        );
        const delta = safeLevel - current;
        if (delta > 0) this.addGems(delta);
        return delta;
      }
      return 0;
    } catch (error) {
      console.error("Failed to update daily max rounds:", error);
      return 0;
    }
  }

  /**
   * Get total accumulated gems (persists across days).
   */
  static getTotalGems(): number {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.GEMS_TOTAL);
      const value = raw ? parseInt(raw, 10) : 0;
      return Number.isFinite(value) && value >= 0 ? value : 0;
    } catch (error) {
      console.error("Failed to get total gems:", error);
      return 0;
    }
  }

  /**
   * Add a non-negative number of gems to the total.
   */
  static addGems(amount: number): void {
    try {
      const safeAmount = Math.max(0, Math.floor(amount));
      if (safeAmount === 0) return;
      const current = this.getTotalGems();
      const next = current + safeAmount;
      localStorage.setItem(STORAGE_KEYS.GEMS_TOTAL, next.toString());
    } catch (error) {
      console.error("Failed to add gems:", error);
    }
  }
}
